// Floating Feedback Button Component
// Fixed position button with Framer Motion animations and unread count badge

import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { MessageCircle, Plus } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { cn } from '@/lib/utils';
import { useFeedbackConversations } from '../hooks/useFeedback';
import { countsTowardBadge } from '../utils/feedbackBuckets';
import type { FeedbackDialogProps } from '../types';

interface FloatingFeedbackButtonProps {
  onOpenDialog: () => void;
  isDialogOpen?: boolean;
}

export const FloatingFeedbackButton: React.FC<FloatingFeedbackButtonProps> = ({
  onOpenDialog,
  isDialogOpen = false
}) => {
  const [isHovered, setIsHovered] = useState(false);
  const [unreadCount, setUnreadCount] = useState(0);

  // Fetch conversations to get unread count
  // Gracefully handle auth errors (401/403) by showing 0 count
  const { data: conversationsData, error: conversationsError } = useFeedbackConversations(
    {
      filters: { status: ['open', 'in_progress'] },
      page_size: 100 // Get all open conversations for unread count
    },
    {
      enabled: !isDialogOpen, // Don't poll when dialog is open
      refetchInterval: 60000 // Check for updates every minute
    }
  );

  // Calculate unread count based on open conversations
  // On error (e.g., auth issues), silently show 0 count
  useEffect(() => {
    if (conversationsError) {
      // Silently handle errors - don't spam console for auth issues
      // Users can still click the button to give feedback
      setUnreadCount(0);
      return;
    }

    if (conversationsData?.conversations) {
      // For now, we'll show count of open conversations as a proxy for "unread"
      // In a full implementation, you'd track actual read status.
      // Confirmed feature requests are parked work and never count (matches the Active tab).
      const openConversations = conversationsData.conversations.filter(countsTowardBadge);
      setUnreadCount(openConversations.length);
    }
  }, [conversationsData, conversationsError]);

  const handleClick = () => {
    onOpenDialog();
  };

  return (
    <AnimatePresence>
      {!isDialogOpen && (
        <motion.div
          initial={{ opacity: 0, scale: 0.8, y: 20 }}
          animate={{ opacity: 1, scale: 1, y: 0 }}
          exit={{ opacity: 0, scale: 0.8, y: 20 }}
          transition={{
            type: 'spring',
            stiffness: 400,
            damping: 25
          }}
          className='fixed bottom-6 right-6 z-50'
          onMouseEnter={() => setIsHovered(true)}
          onMouseLeave={() => setIsHovered(false)}
        >
          <Button
            onClick={handleClick}
            size='lg'
            className={cn(
              'relative h-14 w-14 rounded-full shadow-lg transition-all duration-300',
              'bg-primary hover:bg-primary/90 text-primary-foreground',
              'hover:scale-110 hover:shadow-xl',
              'focus:ring-2 focus:ring-primary/50 focus:ring-offset-2',
              'border-2 border-white/20'
            )}
            aria-label='Open feedback dialog'
          >
            {/* Main icon with animation */}
            <motion.div
              animate={{
                rotate: isHovered ? 360 : 0,
                scale: isHovered ? 1.1 : 1
              }}
              transition={{
                type: 'spring',
                stiffness: 300,
                damping: 20
              }}
            >
              <MessageCircle className='h-6 w-6' />
            </motion.div>

            {/* Plus icon overlay for new feedback */}
            <motion.div
              initial={{ opacity: 0, scale: 0 }}
              animate={{
                opacity: isHovered ? 1 : 0,
                scale: isHovered ? 1 : 0,
                x: isHovered ? 8 : 0,
                y: isHovered ? -8 : 0
              }}
              transition={{
                type: 'spring',
                stiffness: 400,
                damping: 25
              }}
              className='absolute top-0 right-0'
            >
              <Plus className='h-3 w-3 text-primary-foreground' />
            </motion.div>

            {/* Unread count badge */}
            {unreadCount > 0 && (
              <motion.div
                initial={{ opacity: 0, scale: 0 }}
                animate={{ opacity: 1, scale: 1 }}
                exit={{ opacity: 0, scale: 0 }}
                transition={{
                  type: 'spring',
                  stiffness: 500,
                  damping: 30
                }}
                className='absolute -top-2 -right-2'
              >
                <Badge
                  variant='destructive'
                  className={cn(
                    'h-6 w-6 rounded-full p-0 text-xs font-bold',
                    'flex items-center justify-center',
                    'shadow-lg border-2 border-white',
                    'animate-pulse'
                  )}
                >
                  {unreadCount > 99 ? '99+' : unreadCount}
                </Badge>
              </motion.div>
            )}

            {/* Ripple effect on click */}
            <motion.div
              className='absolute inset-0 rounded-full bg-primary-foreground/20'
              initial={{ scale: 0, opacity: 0 }}
              whileTap={{ scale: 1.5, opacity: [0, 0.3, 0] }}
              transition={{ duration: 0.3 }}
            />
          </Button>

          {/* Tooltip */}
          <motion.div
            initial={{ opacity: 0, x: 10 }}
            animate={{
              opacity: isHovered ? 1 : 0,
              x: isHovered ? 0 : 10
            }}
            transition={{ duration: 0.2 }}
            className={cn(
              'absolute right-16 top-1/2 -translate-y-1/2',
              'bg-popover text-popover-foreground',
              'px-3 py-2 rounded-lg shadow-lg',
              'text-sm font-medium whitespace-nowrap',
              'border border-border',
              'pointer-events-none'
            )}
          >
            {unreadCount > 0
              ? `View ${unreadCount} ${unreadCount === 1 ? 'conversation' : 'conversations'}`
              : 'Give feedback'}

            {/* Tooltip arrow */}
            <div className='absolute left-full top-1/2 -translate-y-1/2 w-0 h-0 border-l-8 border-l-popover border-t-4 border-t-transparent border-b-4 border-b-transparent' />
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );
};

export default FloatingFeedbackButton;
